import { Capacitor } from '@capacitor/core';

import { environment } from '../../environments/environment';
import { WHITE_LABEL } from './whitelabel';

/**
 * Which club is this URL for?
 *
 * On the web a club's app is its own ADDRESS: `dublinraiders.huddlesports.ie`
 * is the Raiders' app, in any tab, from any link, on any day. The hostname is
 * the only thing that says whose app this is - not storage, not a previous
 * navigation - which is what makes a link shareable and a fresh tab correct.
 *
 * A path form (`/dublinraiders/sign-in`) is kept as the second answer, for
 * two cases that matter: a club whose subdomain is not wired up yet (DNS,
 * certificate and Firebase Auth's authorized-domains list are all manual,
 * per club), and any browser that will not resolve `*.localhost` in the dev
 * loop. When the club comes from the path, the app is MOUNTED under it via
 * `APP_BASE_HREF` (app.config.ts): Angular strips the base when it reads a
 * URL and writes it back when it navigates, so every route in the app stays
 * the flat one it already was. This module is the whole seam - nothing else
 * needs to know a slug exists.
 *
 * A baked flavour (the native builds) ignores all of it: `WHITE_LABEL` is
 * the club, and the URL never gets a vote.
 */

/** Where a club came from - the base href hangs off this. */
export interface ClubFromUrl {
  readonly slug: string;
  /** `host` = a subdomain (the base stays `/`); `path` = the first segment. */
  readonly source: 'host' | 'path';
}

/**
 * First segments that belong to the APP, so can never be a club. Every
 * route in app.routes.ts whose path could pass {@link SLUG_SHAPE} - the
 * hyphenated ones (`sign-in`, `not-a-member`) could not, but a rename
 * without a thought here would be a club called "signin" shadowing a door.
 */
const RESERVED_PATHS = new Set([
  'sign-in',
  'sign-up',
  'forgot-password',
  'verify-email',
  'invitations',
  'not-a-member',
  // The public payment page. Four letters and lowercase, so without this
  // entry `/give/{token}` would read as a club called "give".
  'give',
  'tabs',
  'auth',
  'assets',
  'index.html',
]);

/** Hostnames that are Huddle's own, not a club's. */
const RESERVED_HOSTS = new Set([
  'app',
  'www',
  'api',
  'hq',
  'admin',
  'staging',
  'dev',
  'development',
  'preview',
]);

/** The shape the slug registry enforces (huddle-kit schema `slugSchema`). */
const SLUG_SHAPE = /^[a-z][a-z0-9]{2,29}$/;

/**
 * The club in a hostname, or null.
 *
 * The first label, when the hostname actually HAS a subdomain to spare:
 * three labels for a real domain (`dublinraiders.huddlesports.ie`), or two
 * under `localhost` (`dublinraiders.localhost` - the dev loop, which
 * Chromium and Firefox resolve to loopback with nothing to set up).
 *
 * The three-label floor is what stops `huddlesports.ie` from reading its
 * own apex as a club called "huddlesports". `app.huddlesports.ie`, an IP
 * address and any label the slug registry would not have issued (the
 * hyphenated `huddle-sports-development.web.app` among them) are null too.
 */
export function clubFromHost(hostname: string): string | null {
  const labels = hostname.split('.').filter(Boolean);
  const isLocal = labels.at(-1) === 'localhost';
  if (labels.length < (isLocal ? 2 : 3)) return null;
  const first = labels[0];
  if (RESERVED_HOSTS.has(first) || !SLUG_SHAPE.test(first)) return null;
  return first;
}

/** The club in a pathname, or null. */
export function clubFromPath(pathname: string): string | null {
  const first = pathname.split('/').filter(Boolean)[0];
  if (!first || RESERVED_PATHS.has(first) || !SLUG_SHAPE.test(first)) return null;
  return first;
}

/** The club this page was opened for - hostname first, then path. */
export function clubFromUrl(hostname: string, pathname: string): ClubFromUrl | null {
  // A baked flavour is already ITS club - the URL never gets a vote.
  if (WHITE_LABEL) return null;
  const host = clubFromHost(hostname);
  if (host) return { slug: host, source: 'host' };
  const path = clubFromPath(pathname);
  return path ? { slug: path, source: 'path' } : null;
}

/** {@link clubFromUrl} for the page this app is running on. */
export function clubFromLocation(): ClubFromUrl | null {
  return clubFromUrl(window.location.hostname, window.location.pathname);
}

/** The club slug this page was opened for, or null (the shared Huddle app). */
export function clubSlugFromLocation(): string | null {
  return clubFromLocation()?.slug ?? null;
}

/**
 * The base every router URL hangs off: `/dublinraiders/` only when the club
 * came from the PATH. A subdomain leaves the app at the root, where it can
 * use its whole address space - which is the point of having one.
 */
export function clubBaseHref(): string {
  const club = clubFromLocation();
  return club?.source === 'path' ? `/${club.slug}/` : '/';
}

/**
 * Where Firebase's account emails should hand the person back.
 *
 * On the WEB, the origin and base this page is running on -
 * `https://dublinraiders.huddlesports.ie/` on a club's own domain,
 * `.../dublinraiders/` on the path form, the bare origin for the shared
 * app. The request is what knows which club it came from; the handler
 * (`/auth/action`) is one fixed URL per Firebase project and cannot.
 *
 * A NATIVE shell's origin is not an address at all - `capacitor://localhost`
 * in a shipped build, the Mac's LAN address under live reload - and Firebase
 * refuses to SEND an email whose continue URL is off the project's
 * authorized-domains list (`auth/unauthorized-continue-uri`, swallowed on
 * sign-up because delivery is retryable). So a native build hands back to
 * the environment's web home instead: the stamped club's path form on
 * `environment.webOrigin`, which is where the emailed link should land a
 * person regardless of which device asked for it.
 */
export function clubContinueUrl(native = Capacitor.isNativePlatform()): string {
  if (native) {
    const slug = WHITE_LABEL?.slug;
    return `${environment.webOrigin}${slug ? `/${slug}/` : '/'}`;
  }
  return `${window.location.origin}${clubBaseHref()}`;
}

/**
 * Is this a URL we are willing to send someone back to?
 *
 * The `continueUrl` on an action link is a query parameter, and a query
 * parameter is somebody else's text until proven otherwise. Firebase will
 * only SEND one whose domain is authorized, but nothing stops a hand-edited
 * link arriving at the handler - so an open redirect is exactly one missing
 * check away. Same host as this page, or a sibling under the same
 * registrable domain (`*.huddlesports.ie`, `*.localhost`), and nothing else.
 */
export function isReturnableUrl(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw, window.location.origin);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
  return (
    url.hostname === window.location.hostname ||
    registrableDomain(url.hostname) === registrableDomain(window.location.hostname)
  );
}

/**
 * The part of a hostname that identifies WHOSE it is: the last two labels
 * for a real domain (`dublinraiders.huddlesports.ie` -> `huddlesports.ie`),
 * and the whole of `localhost` for the dev names, which have no public
 * suffix to spare. Deliberately not a public-suffix-list implementation -
 * these are our own names and nothing else is allowed through.
 */
function registrableDomain(hostname: string): string {
  const labels = hostname.split('.');
  if (labels.at(-1) === 'localhost') return 'localhost';
  return labels.slice(-2).join('.');
}

/**
 * `/dublinraiders` -> `/dublinraiders/`, in place and without a reload.
 *
 * Path form only. The bare slug is what a person types, but a base href of
 * `/dublinraiders/` only strips off a path that carries the trailing slash -
 * without this the router would be handed `/dublinraiders` as a route to
 * match and find nothing. Called once, before bootstrap.
 */
export function normaliseClubPath(): void {
  const club = clubFromLocation();
  if (club?.source !== 'path') return;
  const { pathname, search, hash } = window.location;
  if (pathname !== `/${club.slug}`) return;
  window.history.replaceState({}, '', `/${club.slug}/${search}${hash}`);
}
